import DiscrepancyList from '@components/DiscrepancyList';
import GridListSubheader from '@components/GridListSubheader';
import PendingsList from '@components/PendingsList';
import WolfButton from '@components/ui/WolfButton';
import WolfTooltip from '@components/ui/WolfTooltip';
import { graphQlClient } from '@config/graphqlClient';
import DiscrepanciesGraphQL from '@graphql/discrepancy.queries';
import PendingGraphQL from '@graphql/pending.queries';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import {
  Discrepancy,
  getActiveDiscrepanciesAndPendingCountRequest,
  getActiveDiscrepanciesRequest,
  getDiscrepanciesRequest,
  updateDiscrepancyRequest
} from '@models/discrepancy.model';
import { InfoOutlined } from '@mui/icons-material';
import { Backdrop, Box, CircularProgress, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { setDiscrepancyCount } from '@redux/reducers/badges.reducer';
import { setWolfieEnabled, setWolfieLoading, setWolfieMinimized } from '@redux/reducers/wolfie.reducer';
import { RootState } from '@redux/store';
import trackUserActivity, { ActivityType } from '@utils/ai/trackUserActivity';
import { getDiscrepancyActionBehavior } from '@utils/discrepancyBehaviors';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

const DiscrepanciesList: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  //TODO: Get all possible integrations from backend

  const { isImpersonating } = useAppSelector((state: RootState) => state.impersonate);
  const { rememberMe } = useAppSelector((state: RootState) => state.user);

  const [refresh, setRefresh] = useState(false);

  const [discrepancies, setDiscrepancies] = useState<Discrepancy[]>([]);
  const [pendings, setPendings] = useState<any[]>([]);
  const [isRecheckDisabled, setIsRecheckDisabled] = useState(false);
  const [recheckLoading, setRecheckLoading] = useState(false);
  const [tooltipMessage, setTooltipMessage] = useState('');
  const [loadingPendingId, setLoadingPendingId] = useState<string | null>(null);

  const localeDiscrepancies = useTranslation('discrepancies');
  const localeCommon = useTranslation('common');
  const localeActionMessages = useTranslation('actionMessages');

  useEffect(() => {
    // Check if recheck button should be disabled based on last recheck time
    const lastRecheckTime = localStorage.getItem('lastDiscrepancyRecheck');
    if (lastRecheckTime) {
      const lastTime = parseInt(lastRecheckTime, 10);
      const tenMinutes = 10 * 60 * 1000; // 10 minutes in milliseconds
      const timeSinceLastRecheck = Date.now() - lastTime;

      if (timeSinceLastRecheck < tenMinutes) {
        setIsRecheckDisabled(true);
        setTooltipMessage(
          localeDiscrepancies['recheckDisabledMessage'] ||
            'You recently manually updated. You will be able to do it again in a few minutes.'
        );

        // Set timeout to re-enable button after remaining time
        const remainingTime = tenMinutes - timeSinceLastRecheck;
        setTimeout(() => {
          setIsRecheckDisabled(false);
          setTooltipMessage('');
          localStorage.removeItem('lastDiscrepancyRecheck');
        }, remainingTime);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial data load on mount
  useEffect(() => {
    getDiscrepancies();
    getPendings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshDiscrepancyCount = async () => {
    try {
      const data: getActiveDiscrepanciesAndPendingCountRequest = await graphQlClient.request(
        DiscrepanciesGraphQL.queries.getActiveDiscrepanciesAndPendingCount,
      );
      if (data?.getActiveDiscrepanciesAndPendingCount) {
        const totalCount =
          data.getActiveDiscrepanciesAndPendingCount.activeDiscrepanciesCount +
          data.getActiveDiscrepanciesAndPendingCount.activePendingCount;
        dispatch(setDiscrepancyCount(totalCount));
      }
    } catch (error) {
      console.error('Error refreshing discrepancy count:', error);
    }
  };

  // Refresh discrepancy badge count on page load
  useEffect(() => {
    refreshDiscrepancyCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle window focus refresh
  useEffect(() => {
    const handleFocus = () => {
      if (refresh) {
        getDiscrepancies();
        getPendings();
        setRefresh(false);
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const handleManualRecheck = async () => {
    if (isImpersonating) {
      dispatch(
        appendActionMessage({
          message: localeActionMessages['whileImpersonatingError'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }

    setRecheckLoading(true);
    try {
      await graphQlClient.request(DiscrepanciesGraphQL.queries.recheckDiscrepancies);

      // Show success message
      dispatch(
        appendActionMessage({
          message: localeDiscrepancies['recheckSuccess'] || 'Discrepancies recheck completed successfully',
          type: SnackType.SUCCESS,
        }),
      );

      // Store current time in localStorage and disable button for 10 minutes
      const currentTime = Date.now();
      localStorage.setItem('lastDiscrepancyRecheck', currentTime.toString());
      setIsRecheckDisabled(true);
      setTooltipMessage(
        localeDiscrepancies['recheckDisabledMessage'] ||
          'You recently manually updated. You will be able to do it again in a few minutes.'
      );

      const tenMinutes = 10 * 60 * 1000; // 10 minutes in milliseconds

      // Re-enable button after 10 minutes
      setTimeout(() => {
        setIsRecheckDisabled(false);
        setTooltipMessage('');
        localStorage.removeItem('lastDiscrepancyRecheck');
      }, tenMinutes);

      // Refresh discrepancies list and badge count
      getDiscrepancies();
      refreshDiscrepancyCount();
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setRecheckLoading(false);
    }
  };

  const getPendings = async () => {
    try {
      const data: any = await graphQlClient.request(
        PendingGraphQL.queries.getPendings,
        { statuses: ['PENDING', 'IN_PROGRESS'] }
      );
      if (data?.getPendings) {
        setPendings(data.getPendings);
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  };

  const getDiscrepancies = async (showInactive?: boolean) => {
    try {
      if (showInactive) {
        const data: getDiscrepanciesRequest = await graphQlClient.request(
          DiscrepanciesGraphQL.queries.getDiscrepancies,
        );
        if (data?.getDiscrepancies) {
          setDiscrepancies(data.getDiscrepancies);
          // Don't update count here - Sidebar manages it
        }
      } else {
        const data: getActiveDiscrepanciesRequest = await graphQlClient.request(
          DiscrepanciesGraphQL.queries.getActiveDiscrepancies,
        );
        if (data?.getActiveDiscrepancies) {
          setDiscrepancies(data.getActiveDiscrepancies);
          // Don't update count here - Sidebar manages it
        }
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  };

  const handleShowInactiveDiscrepancies = (show: boolean) => {
    getDiscrepancies(show);
  };

  const handleCompletePending = async (pendingId: string) => {
    if (isImpersonating) {
      dispatch(
        appendActionMessage({
          message: localeActionMessages['whileImpersonatingError'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }

    try {
      const data: any = await graphQlClient.request(
        PendingGraphQL.mutations.updatePendingStatus,
        { pendingId, status: 'COMPLETED' }
      );
      if (data?.updatePendingStatus) {
        const updatedPendings = pendings.filter((pending) => pending.pendingId !== pendingId);
        setPendings(updatedPendings);

        dispatch(
          appendActionMessage({
            message: localeActionMessages['actionCompletedSuccessfully'] || 'Action completed successfully',
            type: SnackType.SUCCESS,
          }),
        );

        // Update badge count
        refreshDiscrepancyCount();
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  };

  const handleContinuePending = async (pendingId: string) => {
    if (isImpersonating) {
      dispatch(
        appendActionMessage({
          message: localeActionMessages['whileImpersonatingError'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }

    try {
      // Set loading state for this specific pending
      setLoadingPendingId(pendingId);

      // Enable Wolfie and show loading state before tracking activity
      dispatch(setWolfieEnabled(true));
      dispatch(setWolfieMinimized(false));

      // Track the continue pending activity
      const result = await trackUserActivity(
        ActivityType.CONTINUE_PENDING,
        rememberMe,
        pendingId
      );

      if (result.success && result.data) {
        // Refresh pendings list to get updated status (moves from PENDING to IN_PROGRESS)
        const pendingsData: any = await graphQlClient.request(
          PendingGraphQL.queries.getPendings,
          { statuses: ['PENDING', 'IN_PROGRESS'] }
        );
        if (pendingsData?.getPendings) {
          setPendings(pendingsData.getPendings);
        }

        // Show success message with pending name if available
        const pendingName = result.data.metadata?.pendingStarted || 'pending action';
        dispatch(
          appendActionMessage({
            message: `${localeDiscrepancies['continuePending']}: ${pendingName}`,
            type: SnackType.SUCCESS,
          }),
        );
      } else {
        dispatch(setWolfieLoading(false));
        dispatch(
          appendActionMessage({
            message: localeCommon['requestError'],
            type: SnackType.ERROR,
          }),
        );
      }
    } catch (e: any) {
      dispatch(setWolfieLoading(false));
      dispatch(
        appendActionMessage({
          message: e?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      // Clear loading state
      setLoadingPendingId(null);
    }
  };

  const updateDiscrepancy = async (
    selected: string,
    discrepancy: Discrepancy,
    ignoreType?: string,
    ignoreDate?: string,
  ) => {
    if (isImpersonating) {
      dispatch(
        appendActionMessage({
          message: localeActionMessages['whileImpersonatingError'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }

    // Check behavior based on discrepancy type
    const behavior = getDiscrepancyActionBehavior(discrepancy, selected);

    // If should navigate without calling backend (e.g., PROCESS_IMPROVEMENT)
    if (behavior.shouldNavigate && !behavior.shouldCallBackend) {
      if (behavior.navigateUrl) {
        history.push(behavior.navigateUrl);
      }
      return;
    }

    if (ignoreType === 'custom' && !ignoreDate) {
      return;
    }
    if (selected === 'IGNORE') {
      let today = new Date();
      let newDate = new Date(today);
      switch (ignoreType) {
        case 'day':
          newDate.setDate(today.getDate() + 1);
          ignoreDate = newDate.toISOString();
          break;
        case 'month':
          newDate.setMonth(today.getMonth() + 1);
          ignoreDate = newDate.toISOString();
          break;
        case 'custom':
          ignoreDate = new Date(ignoreDate!).toISOString();
          break;
        case 'permanently':
          ignoreDate = '-1';
          break;
      }
    }

    const variables = {
      discrepancyId: discrepancy.discrepancyId,
      // integrationType: discrepancy.integrationType,
      // entity: discrepancy.discrepancyData?.entity,
      // entityId: discrepancy.discrepancyData?.entityId,
      action: selected,
      ignoreUntil: ignoreDate,
    };

    // Only call backend if behavior indicates we should
    if (behavior.shouldCallBackend) {
      try {
        const data: updateDiscrepancyRequest = await graphQlClient.request(
          DiscrepanciesGraphQL.mutations.updateDiscrepancy,
          { discrepancy: variables },
        );
        if (data?.updateDiscrepancy) {
          if (data?.updateDiscrepancy?.navigateTo) {
            history.push(data?.updateDiscrepancy?.navigateTo);
          } else {
            const updatedDiscrepancies = discrepancies.filter(
              (discrepancy) => discrepancy.discrepancyId !== variables.discrepancyId
            );
            setDiscrepancies(updatedDiscrepancies);

            dispatch(
              appendActionMessage({
                message: localeActionMessages['discrepancyUpdatedSuccessfully'],
                type: SnackType.SUCCESS,
              }),
            );

            // Update badge count
            refreshDiscrepancyCount();
          }
        }
      } catch (e: any) {
        dispatch(
          appendActionMessage({
            message: e?.response?.errors[0]?.message || localeCommon['requestError'],
            type: SnackType.ERROR,
          }),
        );
      }
    }
  };

  return (
    <Box className="w-full">
      <Box className="sticky top-0 z-30 bg-backgroundUI">
        <GridListSubheader
          title={localeDiscrepancies['title']}
          buttonText={null}
          isGridView={false}
          onToggleView={() => {}}
          onDrawerOpen={() => {}}
          icon="discrepancy"
          showFavGrid={false}
        />
      </Box>

      {/* Pendings Section */}
      <Box className="my-6">
        <Box className="flex items-center gap-2 mb-4">
          <Typography variant="h4">{localeDiscrepancies['pendingsTitle']}</Typography>
          <WolfTooltip title={localeDiscrepancies['pendingsTooltip']} arrow placement="top">
            <InfoOutlined className="text-gray-500 cursor-help" fontSize="small" />
          </WolfTooltip>
        </Box>
        <PendingsList
          pendings={pendings}
          handleCompletePending={handleCompletePending}
          handleContinuePending={handleContinuePending}
          loadingPendingId={loadingPendingId}
        />
      </Box>

      {/* Discrepancy Checks Section */}
      <Box className="my-6">
        <Box className="flex flex-wrap items-center justify-between mb-4">
          <Box className="flex items-center gap-2">
            <Typography variant="h4">{localeDiscrepancies['checksTitle']}</Typography>
            <WolfTooltip title={localeDiscrepancies['checksTooltip']} arrow placement="top">
              <InfoOutlined className="text-gray-500 cursor-help" fontSize="small" />
            </WolfTooltip>
          </Box>
          <Box>
            <WolfTooltip title={isRecheckDisabled ? tooltipMessage : ''} arrow placement="top">
              <span>
                <WolfButton
                  color="primary"
                  variant="outlined"
                  onClick={handleManualRecheck}
                  disabled={isRecheckDisabled || recheckLoading}>
                  {recheckLoading
                    ? localeCommon['loading'] || 'Loading...'
                    : localeDiscrepancies['recheckDiscrepancies']}
                </WolfButton>
              </span>
            </WolfTooltip>
          </Box>
        </Box>
        {discrepancies && (
          <DiscrepancyList
            discrepancies={discrepancies}
            handleOptionSelected={updateDiscrepancy}
            handleShowInactive={handleShowInactiveDiscrepancies}
          />
        )}
      </Box>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loadingPendingId !== null}
      >
        <Box className="flex flex-col items-center gap-4">
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" className="text-white">
            {localeDiscrepancies['continuingPending'] || 'Continuing pending action...'}
          </Typography>
        </Box>
      </Backdrop>
    </Box>
  );
};

export default DiscrepanciesList;
